/**
 *
 * @module paywallManager
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/paywall.md
 *
 */

import Check from 'check-types';

import Logging from './../logging/entry';
import Services from './../services/entry';
import Token from './../token/entry';

const TokenManager = Token.TokenManager;

const PaywallManagerConfiguration = Services.Configuration.PaywallManagerConfiguration;
const PaywallClient = Services.Paywall.PaywallClient;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyObject
} = Check.assert;

/**
 *
 * @class PaywallManager
 * @since 3.9.0
 * @desc Manages the paywall and serves as a go between for the public APIs and the PaywallClient.
 *
 */
export default class PaywallManager {

    /**
     *
     * @param {SDK.Services.Configuration.PaywallManagerConfiguration} options.config
     * @param {SDK.Services.Paywall.PaywallClient} options.client
     * @param {SDK.Token.TokenManager} options.tokenManager
     * @param {SDK.Logging.Logger} [options.logger=Logging]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(options) {

        const errorMessage = {
            ctor: `${this.constructor.name}(options)`,
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get config() {
                return `${this.ctor} needs SDK.Services.Configuration.PaywallManagerConfiguration instance`;
            },
            get client() {
                return `${this.ctor} needs SDK.Services.Paywall.PaywallClient instance`;
            },
            get tokenManager() {
                return `${this.ctor} needs SDK.Token.TokenManager instance`;
            },
            get logger() {
                return `${this.ctor} needs SDK.Logger instance`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { config, client, tokenManager, logger = Logging } = options;

        instanceStrict(config, PaywallManagerConfiguration, errorMessage.config, InvalidArgumentException);
        instanceStrict(client, PaywallClient, errorMessage.client, InvalidArgumentException);
        instanceStrict(tokenManager, TokenManager, errorMessage.tokenManager, InvalidArgumentException);
        instanceStrict(logger, Logging.Logger, errorMessage.logger, InvalidArgumentException);

        /**
         *
         * @access private
         * @since 3.9.0
         * @type {SDK.Services.Configuration.PaywallManagerConfiguration}
         *
         */
        this.config = config;

        /**
         *
         * @access private
         * @since 3.9.0
         * @type {SDK.Services.Paywall.PaywallClient}
         *
         */
        this.client = client;

        /**
         *
         * @access private
         * @since 3.9.0
         * @type {SDK.Token.TokenManager}
         *
         */
        this.tokenManager = tokenManager;

        /**
         *
         * @access private
         * @since 3.9.0
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        this.logger.log(PaywallManager.name, 'Created.');

    }

    /**
     *
     * @access private
     * @since 3.9.0
     * @desc Returns a paywall object containing sku, campaign and account context information for
     * a given country, platform and account/device.
     * @returns {Promise<SDK.Services.Paywall.Paywall>}
     *
     */
    async getPaywall() {

        const { client, accessToken } = this;

        return await client.getPaywall(accessToken);

    }

    /* #region private: */

    /**
     *
     * @access private
     * @desc Grabs a fresh AccessToken from the TokenManager instance
     * @returns {SDK.Token.AccessToken}
     *
     */
    get accessToken() {

        return this.tokenManager.getAccessToken();

    }

}



// WEBPACK FOOTER //
// ./src/paywall/paywallManager.js